import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import ProductFilterBar from './components/ProductFilterBar';
import ProductTable from './components/ProductTable';
import AddProductModal from './components/AddProductModal';
import ProductDetailsModal from './components/ProductDetailsModal';

const Products = () => {
  const [products, setProducts] = useState([
    {
      id: 1,
      name: "Industrial HVAC Filter",
      sku: "HVAC-FLT-001",
      category: "HVAC",
      description: "High-efficiency particulate air filter for industrial HVAC systems",
      price: 89.99,
      cost: 45.00,
      quantity: 156,
      reorderLevel: 50,
      supplier: "AirTech Solutions",
      status: "In Stock",
      lastRestocked: "2026-02-10T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 2,
      name: "Commercial Refrigerant R-410A",
      sku: "REF-R410-25",
      category: "Refrigeration",
      description: "25lb cylinder of R-410A refrigerant for commercial cooling systems",
      price: 325.00,
      cost: 180.00,
      quantity: 42,
      reorderLevel: 20,
      supplier: "CoolTech Distributors",
      status: "In Stock",
      lastRestocked: "2026-02-15T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 3,
      name: "Electrical Circuit Breaker 30A",
      sku: "ELEC-CB-30A",
      category: "Electrical",
      description: "30 Amp single pole circuit breaker for residential and commercial use",
      price: 24.99,
      cost: 12.50,
      quantity: 8,
      reorderLevel: 25,
      supplier: "PowerGrid Supply Co",
      status: "Low Stock",
      lastRestocked: "2026-01-28T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 4,
      name: "Plumbing PVC Pipe 2 inch",
      sku: "PLB-PVC-2IN",
      category: "Plumbing",
      description: "10ft length schedule 40 PVC pipe, 2 inch diameter",
      price: 18.50,
      cost: 8.75,
      quantity: 0,
      reorderLevel: 30,
      supplier: "FlowMaster Plumbing",
      status: "Out of Stock",
      lastRestocked: "2026-01-15T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 5,
      name: "Thermostat Digital Programmable",
      sku: "HVAC-THERM-PRO",
      category: "HVAC",
      description: "7-day programmable digital thermostat with WiFi connectivity",
      price: 149.99,
      cost: 75.00,
      quantity: 89,
      reorderLevel: 40,
      supplier: "SmartHome Technologies",
      status: "In Stock",
      lastRestocked: "2026-02-18T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 6,
      name: "LED Work Light 5000 Lumens",
      sku: "TOOL-LED-5K",
      category: "Tools",
      description: "Portable LED work light with adjustable stand, 5000 lumens output",
      price: 79.99,
      cost: 40.00,
      quantity: 34,
      reorderLevel: 15,
      supplier: "ProTech Equipment",
      status: "In Stock",
      lastRestocked: "2026-02-12T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 7,
      name: "Copper Tubing 3/4 inch",
      sku: "PLB-CU-075",
      category: "Plumbing",
      description: "Type L copper tubing, 3/4 inch diameter, 20ft coil",
      price: 65.00,
      cost: 32.50,
      quantity: 18,
      reorderLevel: 25,
      supplier: "FlowMaster Plumbing",
      status: "Low Stock",
      lastRestocked: "2026-02-05T00:00:00",
      unit: "EA",
      isTrackable: true
    },
    {
      id: 8,
      name: "Safety Gloves Heavy Duty",
      sku: "SAFE-GLV-HD",
      category: "Safety Equipment",
      description: "Cut-resistant heavy duty work gloves, size large",
      price: 12.99,
      cost: 6.00,
      quantity: 245,
      reorderLevel: 100,
      supplier: "SafetyFirst Supplies",
      status: "In Stock",
      lastRestocked: "2026-02-20T00:00:00",
      unit: "EA",
      isTrackable: true
    }
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all'
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const filteredProducts = useMemo(() => {
    return products?.filter(product => {
      const matchesSearch = searchQuery === '' ||
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.supplier?.toLowerCase()?.includes(searchQuery?.toLowerCase());

      const matchesCategory = filters?.category === 'all' || product?.category === filters?.category;
      const matchesStatus = filters?.status === 'all' || product?.status === filters?.status;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchQuery, filters]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setIsAddModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setIsAddModalOpen(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsDetailsModalOpen(true);
  };

  const handleSaveProduct = (productData) => {
    if (selectedProduct) {
      setProducts(prev => prev?.map(p => p?.id === selectedProduct?.id ? { ...p, ...productData } : p));
    } else {
      const newProduct = {
        id: Date.now(),
        ...productData,
        lastRestocked: new Date()?.toISOString()
      };
      setProducts(prev => [...prev, newProduct]);
    }
  };

  const handleDeleteProduct = (productId) => {
    setProducts(prev => prev?.filter(p => p?.id !== productId));
  };

  const stats = useMemo(() => {
    const totalProducts = products?.length;
    const totalValue = products?.reduce((sum, p) => sum + (p?.price * p?.quantity), 0);
    const lowStockCount = products?.filter(p => p?.status === 'Low Stock' || p?.status === 'Out of Stock')?.length;
    const inStockCount = products?.filter(p => p?.status === 'In Stock')?.length;

    return { totalProducts, totalValue, lowStockCount, inStockCount };
  }, [products]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              Product Catalog
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your inventory and product information
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Package" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Products</p>
                <p className="text-2xl font-heading font-bold text-foreground">{stats?.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={24} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-heading font-bold text-foreground">${stats?.totalValue?.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Stock</p>
                <p className="text-2xl font-heading font-bold text-foreground">{stats?.inStockCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Low Stock</p>
                <p className="text-2xl font-heading font-bold text-foreground">{stats?.lowStockCount}</p>
              </div>
            </div>
          </div>
        </div>

        <ProductFilterBar
          onSearch={handleSearch}
          onFilter={handleFilter}
          onAddProduct={handleAddProduct}
        />

        <ProductTable
          products={filteredProducts}
          onView={handleViewProduct}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
        />
      </main>

      {isAddModalOpen && (
        <AddProductModal
          product={selectedProduct}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleSaveProduct}
        />
      )}

      {isDetailsModalOpen && selectedProduct && (
        <ProductDetailsModal
          product={selectedProduct}
          onClose={() => setIsDetailsModalOpen(false)}
          onEdit={() => {
            setIsDetailsModalOpen(false);
            setIsAddModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default Products;